import React, {
  lazy,
  ReactElement,
  Suspense,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { reportError } from 'lib/reportError';
import {
  readSkeletonPalette,
  recolorAnimation,
  useResolvedTheme,
} from 'lib/skeletonTheme';

const SkeletonPlaceholder = (): ReactElement => (
  <div
    aria-hidden="true"
    style={{ height: '100%', background: 'var(--sk-background-neutral)' }}
  />
);

const Lottie = lazy(() =>
  import('lottie-react').catch(error => {
    reportError(error, 'Skeleton.animation');
    return { default: SkeletonPlaceholder };
  }),
);

type SkeletonPropsType = {
  animationData: any;
  height?: string;
};

const Skeleton = ({
  animationData,
  height = '442px',
}: SkeletonPropsType): ReactElement => {
  const hostRef = useRef<HTMLDivElement>(null);
  const theme = useResolvedTheme();
  const [themedData, setThemedData] = useState<unknown>(null);

  // The animations ship with fixed light greys. Recolour them from the theme's own
  // roles, over the surface they sit on, before first paint and whenever it changes.
  useLayoutEffect(() => {
    const palette = hostRef.current ? readSkeletonPalette(hostRef.current) : null;
    setThemedData(palette ? recolorAnimation(animationData, palette) : animationData);
  }, [animationData, theme]);

  return (
    <div ref={hostRef} style={{ height }}>
      {themedData ? (
        <Suspense fallback={<SkeletonPlaceholder />}>
          <Lottie animationData={themedData} />
        </Suspense>
      ) : (
        <SkeletonPlaceholder />
      )}
    </div>
  );
};

export { Skeleton };
